import { useState, useCallback, useRef } from 'react'
import type { SelfTestMode } from '@/lib/patterns/types'

export type { SelfTestMode }

const INITIAL_SELF_TEST_MODE: SelfTestMode = {
  enabled: false,
  selectedPanelX: 1,
  selectedPanelY: 1,
  trailMode: false,
  showBorders: true,
  activePanels: new Set<string>()
}

export function useSelfTest() {
  const [selfTestMode, setSelfTestMode] = useState<SelfTestMode>(INITIAL_SELF_TEST_MODE)
  const [cyanPanels, setCyanPanels] = useState(new Set<string>())
  const [subdivisionMode, setSubdivisionMode] = useState(false)
  const [redZones, setRedZones] = useState(new Set<string>())
  const [selectedQuadrant, setSelectedQuadrant] = useState<'tl' | 'tr' | 'bl' | 'br'>('tl')

  const cyanPanelsRef = useRef(cyanPanels)
  cyanPanelsRef.current = cyanPanels

  const resetSelfTest = useCallback(() => {
    setSelfTestMode(INITIAL_SELF_TEST_MODE)
    setCyanPanels(new Set<string>())
    setSubdivisionMode(false)
    setRedZones(new Set<string>())
    setSelectedQuadrant('tl')
  }, [])

  const enableSelfTest = useCallback(() => {
    const panelKey = '1,1'
    setSelfTestMode(prev => ({
      ...prev,
      enabled: true,
      selectedPanelX: 1,
      selectedPanelY: 1,
      showBorders: true,
      activePanels: new Set([panelKey])
    }))
    setCyanPanels(new Set())
    setSubdivisionMode(false)
    setRedZones(new Set())
    setSelectedQuadrant('tl')
  }, [])

  const disableSelfTest = useCallback(() => {
    setSelfTestMode(prev => ({
      ...prev,
      enabled: false,
      showBorders: true,
      activePanels: new Set()
    }))
    setCyanPanels(new Set())
    setSubdivisionMode(false)
    setRedZones(new Set())
    setSelectedQuadrant('tl')
  }, [])

  const toggleCyanPanel = useCallback((panelX: number, panelY: number) => {
    const panelKey = `${panelX},${panelY}`

    setCyanPanels(prev => {
      const next = new Set(prev)
      if (next.has(panelKey)) {
        next.delete(panelKey)
        setRedZones(prevRed => {
          const newRed = new Set(prevRed)
          for (const zoneKey of newRed) {
            if (zoneKey.startsWith(panelKey + ',')) {
              newRed.delete(zoneKey)
            }
          }
          return newRed
        })
      } else {
        next.add(panelKey)
        setSubdivisionMode(true)
      }
      return next
    })
  }, [])

  const toggleRedZone = useCallback((panelX: number, panelY: number, quadrant: 'tl' | 'tr' | 'bl' | 'br') => {
    const panelKey = `${panelX},${panelY}`
    if (!cyanPanelsRef.current.has(panelKey)) return

    setSubdivisionMode(true)

    const zoneKey = `${panelX},${panelY},${quadrant}`
    setRedZones(prev => {
      const next = new Set(prev)
      if (next.has(zoneKey)) {
        next.delete(zoneKey)
      } else {
        next.add(zoneKey)
      }
      return next
    })
  }, [])

  return {
    selfTestMode,
    setSelfTestMode,
    cyanPanels,
    setCyanPanels,
    subdivisionMode,
    setSubdivisionMode,
    redZones,
    setRedZones,
    selectedQuadrant,
    setSelectedQuadrant,
    resetSelfTest,
    enableSelfTest,
    disableSelfTest,
    toggleCyanPanel,
    toggleRedZone,
  }
}
